<template>
  <div class="social-sign-in">
    <button
      type="button"
      class="social-sign-in__btn"
      :disabled="busy"
      @click="signInWithGoogle"
    >
      <svg class="social-sign-in__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Continue with Google
    </button>

    <button
      type="button"
      class="social-sign-in__btn"
      :disabled="busy"
      @click="signInWithApple"
    >
      <svg class="social-sign-in__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="#000"/>
      </svg>
      Continue with Apple
    </button>

    <button
      type="button"
      class="social-sign-in__btn"
      :disabled="busy"
      @click="signInWithMicrosoft"
    >
      <svg class="social-sign-in__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.4 24H0V12.6h11.4V24z" fill="#F1511B"/>
        <path d="M24 24H12.6V12.6H24V24z" fill="#80CC28"/>
        <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#00ADEF"/>
        <path d="M24 11.4H12.6V0H24v11.4z" fill="#FBBC09"/>
      </svg>
      Continue with Microsoft
    </button>
  </div>
</template>

<script>
import { PublicClientApplication } from '@azure/msal-browser';

let msalInstance = null;

export default {
  name: 'SocialSignInButtons',

  data() {
    return {
      busy: false,
    };
  },

  mounted() {
    this._initMicrosoft();
  },

  methods: {
    // ─── Google ───────────────────────────────────────────────────────────────
    async signInWithGoogle() {
      if (this.busy) return;
      this.busy = true;

      try {
        const accessToken = await this._getGoogleToken();
        this.$emit('success', { provider: 'google', providerToken: accessToken });
      } catch (err) {
        if (err && err.type === 'popup_closed') {
          this.busy = false;
          return;
        }
        this.$emit('error', { provider: 'google', message: this._friendlyError(err) });
      } finally {
        this.busy = false;
      }
    },

    _getGoogleToken() {
      return new Promise((resolve, reject) => {
        if (!window.google || !window.google.accounts) {
          reject(new Error('Google SDK not loaded'));
          return;
        }
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          callback: (response) => {
            if (response.error) {
              reject(response);
            } else {
              resolve(response.access_token);
            }
          },
          error_callback: (err) => {
            reject(err);
          },
        });
        client.requestAccessToken();
      });
    },

    // ─── Apple ────────────────────────────────────────────────────────────────
    async signInWithApple() {
      if (this.busy) return;
      this.busy = true;

      if (!window.AppleID) {
        this.$emit('error', { provider: 'apple', message: 'Apple Sign In is not available. Please try again.' });
        this.busy = false;
        return;
      }

      window.AppleID.auth.init({
        clientId: process.env.VUE_APP_APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: process.env.VUE_APP_APPLE_REDIRECT_URI,
        usePopup: true,
      });

      try {
        const response = await window.AppleID.auth.signIn();
        const providerToken = response.authorization.id_token;
        const firstName = response.user?.name?.firstName || undefined;
        const lastName = response.user?.name?.lastName || undefined;

        this.$emit('success', { provider: 'apple', providerToken, firstName, lastName });
      } catch (err) {
        // User cancelled the popup — don't treat as an error worth showing
        if (err && err.error === 'popup_closed_by_user') {
          this.busy = false;
          return;
        }
        this.$emit('error', { provider: 'apple', message: this._friendlyError(err) });
      } finally {
        this.busy = false;
      }
    },

    // ─── Microsoft ────────────────────────────────────────────────────────────
    async _initMicrosoft() {
      if (msalInstance || !process.env.VUE_APP_MICROSOFT_CLIENT_ID) return;

      try {
        msalInstance = await PublicClientApplication.createPublicClientApplication({
          auth: {
            clientId: process.env.VUE_APP_MICROSOFT_CLIENT_ID,
            authority: `https://login.microsoftonline.com/${process.env.VUE_APP_MICROSOFT_TENANT_ID || 'common'}`,
            redirectUri: window.location.origin,
          },
          cache: {
            cacheLocation: 'sessionStorage',
          },
        });
      } catch {
        console.error('Microsoft SDK init failed');
      }
    },

    async signInWithMicrosoft() {
      if (this.busy) return;
      this.busy = true;

      try {
        const accessToken = await this._getMicrosoftToken();
        this.$emit('success', { provider: 'microsoft', providerToken: accessToken });
      } catch (err) {
        // User cancelled the popup
        if (err && err.errorCode === 'user_cancelled') {
          this.busy = false;
          return;
        }
        this.$emit('error', { provider: 'microsoft', message: this._friendlyError(err) });
      } finally {
        this.busy = false;
      }
    },

    async _getMicrosoftToken() {
      if (!msalInstance) {
        msalInstance = await PublicClientApplication.createPublicClientApplication({
          auth: {
            clientId: process.env.VUE_APP_MICROSOFT_CLIENT_ID,
            authority: `https://login.microsoftonline.com/${process.env.VUE_APP_MICROSOFT_TENANT_ID || 'common'}`,
            redirectUri: window.location.origin,
          },
          cache: {
            cacheLocation: 'sessionStorage',
          },
        });
      }

      const response = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email', 'User.Read'],
      });

      return response.accessToken;
    },

    // ─── Shared ───────────────────────────────────────────────────────────────
    _friendlyError(err) {
      return (err && err.message) || 'Something went wrong. Please try again.';
    },
  },
};

</script>

<style scoped>
.social-sign-in {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.social-sign-in__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 11px 16px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  font-size: 0.95rem;
  font-weight: 500;
  color: #1a1a2e;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.social-sign-in__btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.social-sign-in__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.social-sign-in__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
</style>
